import type React from 'react';

import { useGetCard, useGetFace } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model';
import { proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { FaceSample, Tag } from '@vlabs/uikit';

import { GoToFaceDetailsLink } from '@vlabs/monokit/features/go-to-face-details';

import { ListLinkPoint3 } from '../../list-link/ListLink.point3';
import { useEventCardTranslation } from '../i18n';
import st from './shared.module.sass';

interface FaceMatchCardPoint3Props {
  faceId: FaceId;
  similarity?: number;
}

function FaceMatchCardPoint3({
  faceId,
  similarity = undefined,
}: FaceMatchCardPoint3Props): React.JSX.Element | null {
  const { t } = useEventCardTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['face_id', 'card_id', 'create_time', 'avatar'] });
  const cardId = face?.card_id;
  const { data: card } = useGetCard(cardId ?? '', {}, {
    query: { enabled: !!cardId },
  });

  if (!face) return null;

  const sampleSrc = proxyArtemyUrl(face.avatar);
  const listIds = card?.lists ?? [];

  return (
    <div className={st.Card}>
      <div className={st.PrimarySection}>
        <FaceSample
          className={st.Sample}
          src={sampleSrc}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {similarity != null && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="primary"
              size="large"
              title={t('similarity_face')}
              value={similarity}
            />
          )}
          <div className={st.Date}>{formatDateTime(face.create_time, DATE_FORMATS.withYear)}</div>
        </div>
      </div>
      <div className={st.SecondarySection}>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('face_id')}</div>
          <div className={st.RowValue}>{face.face_id ?? '-'}</div>
        </div>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('lists')}</div>
          <TruncatedText
            hasTruncation
            tooltipContent={listIds.join(', ')}
          >
            <ListLinkPoint3 listIds={listIds} />
          </TruncatedText>
        </div>
      </div>
      {!!face.face_id && (
        <GoToFaceDetailsLink
          className={st.Link}
          faceId={face.face_id}
        />
      )}
    </div>
  );
}

export { FaceMatchCardPoint3 };
